import { Helmet } from "react-helmet";
import { useState, useEffect, useRef } from "react";
import DashboardTopbar from "components/DashboardTopbar";
import DashboardMenuBar from "components/DashboardMenuBar";
import { useAppContext } from "components/Context/AppContext";
import AllocBarCanvas from "components/AllocBarCanvas";
import {
  AreaChart, Area, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import "styles/dashboard.css";

const DASHBOARD_API =
  process.env.REACT_APP_API_URL || "http://localhost:5050/api";

// ── Colour helpers ────────────────────────────────────────────────────────────
function hexToRgb(h) {
  const n = parseInt(h.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function lerpColour(a, b, t) {
  const ca = hexToRgb(a), cb = hexToRgb(b);
  return `rgb(${Math.round(ca[0] + (cb[0] - ca[0]) * t)},${Math.round(ca[1] + (cb[1] - ca[1]) * t)},${Math.round(ca[2] + (cb[2] - ca[2]) * t)})`;
}

// ── Gauge colour functions ────────────────────────────────────────────────────
const SHARPE_TARGET = 0.15, SHARPE_BAND = 0.05;

function makeRiskColour(limit) {
  const MAX = limit * 1.15;
  const BAND = limit * 0.05;
  return (v) => {
    const p = v / MAX;
    const pY = (limit - BAND) / MAX, pR = (limit + BAND) / MAX;
    if (p <= pY) return lerpColour("#22d47e", "#C9A84C", Math.min(1, p / pY));
    if (p <= pR) return lerpColour("#C9A84C", "#e05252", (p - pY) / (pR - pY));
    return "#e05252";
  };
}

function concColour(ratio) {
  return ratio <= 0.95 ? "#22d47e" : ratio <= 1.05 ? "#C9A84C" : "#e05252";
}

// ── SVG Gauge ────────────────────────────────────────────────────────────────
function SvgGauge({ min = 0, max, value, target, targetLabel, colourFn, reading, readingColor }) {
  const cx = 140, cy = 148, r = 105, SEGS = 100;
  const toDeg = (v) => 180 - ((v - min) / (max - min)) * 180;
  const toXY = (deg, radius) => ({
    x: cx + radius * Math.cos((deg * Math.PI) / 180),
    y: cy - radius * Math.sin((deg * Math.PI) / 180),
  });

  const segments = [];
  const ptL = toXY(180, r), ptR = toXY(0, r);
  segments.push(
    <path key="bg"
      d={`M ${ptL.x.toFixed(2)} ${ptL.y.toFixed(2)} A ${r} ${r} 0 0 1 ${ptR.x.toFixed(2)} ${ptR.y.toFixed(2)}`}
      fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="15" strokeLinecap="butt"
    />
  );
  for (let i = 0; i < SEGS; i++) {
    const v0 = min + (i / SEGS) * (max - min);
    const v1 = min + ((i + 1) / SEGS) * (max - min);
    const p0 = toXY(toDeg(v0), r), p1 = toXY(toDeg(v1), r);
    segments.push(
      <path key={i}
        d={`M ${p0.x.toFixed(2)} ${p0.y.toFixed(2)} A ${r} ${r} 0 0 1 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`}
        fill="none" stroke={colourFn((v0 + v1) / 2)} strokeWidth="15" strokeLinecap="butt"
      />
    );
  }

  const clampedValue = Math.max(min, Math.min(max, value));
  const tip   = toXY(toDeg(clampedValue), r - 5);
  const ta    = toDeg(target);
  const tOuter = toXY(ta, r + 11), tInner = toXY(ta, r - 11);
  const lp    = toXY(ta, r + 24);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <svg viewBox="0 0 280 148" width="100%"
        style={{ display: "block", overflow: "visible", maxWidth: 190 }}>
        <defs>
          <filter id="nd-shadow">
            <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor="rgba(0,0,0,0.5)" />
          </filter>
        </defs>
        {segments}
        <line
          x1={cx} y1={cy} x2={tip.x.toFixed(2)} y2={tip.y.toFixed(2)}
          stroke="rgba(255,255,255,0.9)" strokeWidth="2" strokeLinecap="round"
          filter="url(#nd-shadow)"
        />
        <circle cx={cx} cy={cy} r="5.5" fill="#080e18" stroke="rgba(255,255,255,0.55)" strokeWidth="1.5" />
        <circle cx={cx} cy={cy} r="2" fill="rgba(255,255,255,0.7)" />
        <line
          x1={tOuter.x.toFixed(2)} y1={tOuter.y.toFixed(2)}
          x2={tInner.x.toFixed(2)} y2={tInner.y.toFixed(2)}
          stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" strokeLinecap="round"
        />
        <text
          x={lp.x.toFixed(1)} y={lp.y.toFixed(1)}
          fontSize="12" fill="var(--dash-muted)"
          textAnchor="middle" fontFamily="'DM Mono', monospace"
        >
          {targetLabel ?? target}
        </text>
      </svg>
      <div style={{
        fontFamily: "var(--dash-font-d)", fontSize: "1.3rem",
        color: readingColor, textAlign: "center", lineHeight: 1, marginTop: -2,
      }}>
        {reading}
      </div>
    </div>
  );
}


// ── Chart tooltip ────────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "rgba(8,14,24,0.96)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, padding: "7px 10px", fontSize: "0.65rem", fontFamily: "var(--dash-font-m)" }}>
      <div style={{ color: "rgba(255,255,255,0.4)", marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: "rgba(255,255,255,0.85)" }}>
          {p.name}: {p.value != null ? `${p.value.toFixed(1)}M` : "—"}
        </div>
      ))}
    </div>
  );
}

// ── Top Risk Contributors/Hedges — mock data (max 10) ────────────────────────
const TOP_RISK_DATA = [
  { name: "SPY",                    pct:  7.6 },
  { name: "iShares MSCI Emerging",  pct:  5.6 },
  { name: "RSP",                    pct:  4.2 },
  { name: "EWJ",                    pct:  3.4 },
  { name: "FIAE LP65145998",        pct:  2.8 },
  { name: "QQQ",                    pct:  2.1 },
  { name: "VEA",                    pct:  1.7 },
  { name: "TLT",                    pct: -2.1 },
  { name: "GLD",                    pct: -1.4 },
  { name: "UVXY",                   pct: -0.8 },
];

function TopRiskBar({ data = TOP_RISK_DATA }) {
  const maxAbs = Math.max(...data.map(d => Math.abs(d.pct)));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem", flex: 1, minHeight: 0, justifyContent: "space-evenly", padding: "0 1rem 0.8rem" }}>
      {data.map((d) => {
        const isHedge   = d.pct < 0;
        const barWidth  = `${(Math.abs(d.pct) / maxAbs * 100).toFixed(1)}%`;
        const barColour = isHedge ? "var(--dash-gold-text)" : "var(--dash-blue)";
        return (
          <div key={d.name} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Name */}
            <div style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.6rem", fontWeight: 500, width: 130, flexShrink: 0, color: "var(--dash-text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {d.name}
            </div>
            {/* Bidirectional bar track */}
            <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
              {/* Left half (hedges) */}
              <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                {isHedge && (
                  <div style={{ width: barWidth, height: 14, background: barColour, borderRadius: "3px 0 0 3px", opacity: 0.85 }} />
                )}
              </div>
              {/* Centre line */}
              <div style={{ width: 1, height: 16, background: "rgba(255,255,255,0.15)", flexShrink: 0, margin: "0 1px" }} />
              {/* Right half (contributors) */}
              <div style={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
                {!isHedge && (
                  <div style={{ width: barWidth, height: 14, background: barColour, borderRadius: "0 3px 3px 0", opacity: 0.85 }} />
                )}
              </div>
            </div>
            {/* Value */}
            <div style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.6rem", width: 38, textAlign: "right", color: isHedge ? "var(--dash-gold-text)" : "var(--dash-text)", flexShrink: 0 }}>
              {d.pct > 0 ? "+" : ""}{d.pct.toFixed(1)}%
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Broker dot colours for the accounts table ────────────────────────────────
const BROKER_COLORS = {
  "Interactive Brokers": "#4e8fdd",
  "Fidelity":            "#22d47e",
  "Charles Schwab":      "#C9A84C",
  "Goldman Sachs":       "#b87bc8",
  "Morgan Stanley":      "#e05252",
};
const DEFAULT_BROKER_COLOR = "#5a7090";

// ── Main page ─────────────────────────────────────────────────────────────────
export default function SummaryPage() {
  const { selectedAccount } = useAppContext();
  const [theme, setTheme]             = useState("dark");
  const [activeRange, setActiveRange] = useState("1Y");
  const [metrics, setMetrics]         = useState({});
  const [asOfDate, setAsOfDate]       = useState(null);
  const [chartPoints, setChartPoints] = useState([]);
  const [brokerRows, setBrokerRows]   = useState([]);
  const [concRows, setConcRows]       = useState([]);
  const [gaugeData, setGaugeData]     = useState(null);
  const [allocLevels, setAllocLevels] = useState(null);   // all drill-down levels, loaded once
  const [allocCurrentLevel, setAllocCurrentLevel] = useState("all");
  const [fsPanel, setFsPanel]         = useState(null);   // { title, content }

  // ── Fetch on account change ──
  useEffect(() => {
    if (!selectedAccount) return;
    const token  = sessionStorage.getItem("token");
    const params = `account_id=${selectedAccount.account_id}&token=${token}`;

    fetch(`${DASHBOARD_API}/summary/metrics?${params}`)
      .then((r) => r.json())
      .then((d) => { setMetrics(d); setAsOfDate(d.asOfDate || null); })
      .catch(console.error);

    fetch(`${DASHBOARD_API}/summary/brokers?${params}`)
      .then((r) => r.json())
      .then(setBrokerRows)
      .catch(console.error);

    fetch(`${DASHBOARD_API}/summary/concentrations?${params}`)
      .then((r) => r.json())
      .then(setConcRows)
      .catch(console.error);

    fetch(`${DASHBOARD_API}/summary/gauges?${params}`)
      .then((r) => r.json())
      .then(setGaugeData)
      .catch(console.error);

    fetch(`${DASHBOARD_API}/summary/allocation?${params}`)
      .then((r) => r.json())
      .then(setAllocLevels)
      .catch(console.error);
  }, [selectedAccount]);

  // ── Fetch chart on range / account change ──
  useEffect(() => {
    if (!selectedAccount) return;
    const token  = sessionStorage.getItem("token");
    const params = `account_id=${selectedAccount.account_id}&token=${token}`;
    fetch(`${DASHBOARD_API}/summary/chart/${activeRange}?${params}`)
      .then((r) => r.json())
      .then((pts) =>
        setChartPoints(pts.map((p) => ({
          label: p.label,
          value: p.value != null ? p.value / 1e6 : null,
          bmk:   p.bmk   != null ? p.bmk   / 1e6 : null,
        })))
      )
      .catch(console.error);
  }, [activeRange, selectedAccount]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const fmtM = (v) => v != null ? `${(v / 1e6).toFixed(1)}` : "—";
  const fmtK = (v) => v != null ? `${(v / 1e3).toFixed(1)}K` : "—";
  const fmtPct = (v) => v != null ? `${v >= 0 ? "+" : ""}${v.toFixed(2)}%` : "—";

  const aum         = metrics.aum;
  const unrealized  = metrics.unrealizedGain;
  const dayReturn   = metrics.dayReturn;
  const ytdReturn   = metrics.ytdReturn;
  const var1d95     = metrics.var1d95;
  const volatility  = metrics.volatility;
  const beta        = metrics.beta;
  const sharpeVal   = metrics.sharpe;

  // Gauge data (fall back to mock if not yet loaded)
  const sharpeGauge = gaugeData?.sharpe ?? { value: sharpeVal ?? 0.21, target: 0.15, band: 0.05 };
  const SHARPE_MAX  = Math.max(0.50, sharpeGauge.value * 1.25);
  const sharpeColour = (v) => {
    const p = v / SHARPE_MAX;
    const lo = (SHARPE_TARGET - SHARPE_BAND) / SHARPE_MAX;
    const hi = (SHARPE_TARGET + SHARPE_BAND) / SHARPE_MAX;
    if (p <= lo) return "#e05252";
    if (p <= hi) return lerpColour("#e05252", "#C9A84C", (p - lo) / (hi - lo));
    return lerpColour("#C9A84C", "#22d47e", Math.min(1, (p - hi) / (1 - hi)));
  };
  const varGauge    = gaugeData?.varLimit ?? { value: var1d95 ?? 16900000, limit: 25000000, band: 1250000 };
  const varLimit    = varGauge.limit;
  const varMax      = varLimit * 1.15;
  const riskColour  = makeRiskColour(varLimit);

  // Gauge reading colours
  const sharpeDiff = sharpeGauge.value - sharpeGauge.target;
  const sharpeReadColor = sharpeDiff > sharpeGauge.band ? "#22d47e"
    : sharpeDiff > -sharpeGauge.band ? "#C9A84C" : "#e05252";

  const varDiff = varGauge.value - varGauge.limit;
  const varReadColor = varDiff < -varGauge.band ? "#22d47e"
    : varDiff < varGauge.band ? "#C9A84C" : "#e05252";

  // Total row for accounts table
  const totalMV  = brokerRows.reduce((s, b) => s + (b.marketValue || 0), 0);
  const totalVar = brokerRows.reduce((s, b) => s + (b.var1d || 0), 0);

  // Fullscreen
  const openFs = (title, content) => setFsPanel({ title, content });
  const closeFs = () => setFsPanel(null);

  // As-of date display
  const asOfDisplay = asOfDate
    ? new Date(asOfDate + "T00:00:00").toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
      })
    : "—";

  // Fund name from account
  const fundName = selectedAccount?.account_name || selectedAccount?.short_name || "Portfolio";

  // ── Concentrations fullscreen content ──────────────────────────────────────
  const ConcContent = ({ rows, maxRatio }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, padding: "1rem 0" }}>
      {rows.map((d) => {
        const c = concColour(d.ratio);
        const vp = (d.ratio / maxRatio * 100).toFixed(2);
        const lp = (1 / maxRatio * 100).toFixed(2);
        return (
          <div key={d.category} style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.75rem", width: 100, color: "var(--dash-text2)" }}>
              {d.category}
            </div>
            <div style={{ flex: 1, position: "relative", height: 20 }}>
              <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 1, background: "var(--dash-border)", transform: "translateY(-50%)" }} />
              <div style={{ position: "absolute", top: "50%", left: 0, width: `${vp}%`, height: 2, background: c, opacity: 0.3, transform: "translateY(-50%)" }} />
              <div style={{ position: "absolute", top: "50%", left: `${vp}%`, width: 10, height: 10, borderRadius: "50%", background: c, transform: "translate(-50%,-50%)", boxShadow: `0 0 6px ${c}88` }} />
              <div style={{ position: "absolute", top: 2, bottom: 2, left: `${lp}%`, width: 1.5, background: "var(--dash-border2)", transform: "translateX(-50%)" }} />
            </div>
            <div style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.75rem", color: c, width: 36, textAlign: "right" }}>
              {d.ratio < 1 ? d.ratio.toFixed(2) : d.ratio.toFixed(1)}x
            </div>
          </div>
        );
      })}
    </div>
  );

  const concMax = concRows.length ? Math.max(...concRows.map((d) => d.ratio)) * 1.1 : 5;

  return (
    <>
      <Helmet>
        <title>Summary – Tail Risk Global</title>
      </Helmet>

      <div className={`trg-shell${theme === "light" ? " theme-light" : ""}`}>

        {/* Top bar */}
        <DashboardTopbar />

        {/* Tab bar */}
        <DashboardMenuBar onThemeChange={setTheme} />

        {/* Body */}
        <main className="trg-body">

          {/* Fund header */}
          <div className="trg-fund-header">
            <div className="trg-fund-left">
              <div className="trg-fund-eyebrow">Portfolio Summary</div>
              <div className="trg-fund-divider" />
              <div className="trg-fund-name">{fundName}</div>
            </div>
            <div className="trg-fund-date">As of {asOfDisplay}</div>
          </div>

          {/* 4-col grid */}
          <div className="trg-main-grid">

            {/* ── COL 1: Performance ────────────────────────────────────── */}
            <div className="trg-card trg-perf-col">
              <div className="trg-card-header">
                <div className="trg-card-title">Performance</div>
              </div>
              <div className="trg-perf-metrics">
                <div className="trg-metric-row">
                  <div className="trg-metric-label">Market Value</div>
                  <div className="trg-metric-value">
                    {aum != null ? (aum / 1e6).toFixed(1) : "—"}
                    <span className="unit">M</span>
                  </div>
                </div>
                <div className="trg-metric-row">
                  <div className="trg-metric-label">Unrealized Gain</div>
                  <div className={`trg-metric-value${unrealized != null ? (unrealized >= 0 ? " green" : " red") : ""}`}>
                    {unrealized != null ? `${unrealized >= 0 ? "+" : ""}${(unrealized / 1e6).toFixed(1)}` : "—"}
                    {unrealized != null && <span className="unit">M</span>}
                  </div>
                  <div className="trg-metric-sub">since inception</div>
                </div>
                <div className="trg-metric-row">
                  <div className="trg-metric-label">Today's Return</div>
                  <div className={`trg-metric-value${dayReturn != null ? (dayReturn >= 0 ? " green" : " red") : ""}`}>
                    {dayReturn != null ? `${dayReturn >= 0 ? "+" : ""}${dayReturn.toFixed(2)}%` : "—"}
                  </div>
                  <div className="trg-metric-sub">vs prior close</div>
                </div>
                <div className="trg-metric-row">
                  <div className="trg-metric-label">YTD Return</div>
                  <div className={`trg-metric-value${ytdReturn != null ? (ytdReturn >= 0 ? " green" : " red") : ""}`}>
                    {ytdReturn != null ? `${ytdReturn >= 0 ? "+" : ""}${ytdReturn.toFixed(1)}%` : "—"}
                  </div>
                  <div className="trg-metric-sub">year to date</div>
                </div>
              </div>
            </div>

            {/* ── COL 2: Risk ───────────────────────────────────────────── */}
            <div className="trg-card trg-risk-col">
              <div className="trg-card-header">
                <div className="trg-card-title">Risk</div>
                <div style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.55rem", color: "var(--dash-muted2)" }}>USD</div>
              </div>

              {/* Risk strip */}
              <div className="trg-risk-strip">
                <div className="trg-risk-strip-item">
                  <div className="trg-risk-strip-label">VaR 95% 1D</div>
                  <div className="trg-risk-strip-value">
                    {var1d95 != null
                      ? Math.abs(var1d95) >= 1e6
                        ? (var1d95 / 1e6).toFixed(1)
                        : (var1d95 / 1e3).toFixed(1)
                      : "—"}
                    <span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.6rem", color: "var(--dash-muted)", marginLeft: 2 }}>
                      {var1d95 != null ? (Math.abs(var1d95) >= 1e6 ? "M" : "K") : ""}
                    </span>
                  </div>
                </div>
                <div className="trg-risk-strip-item">
                  <div className="trg-risk-strip-label">Volatility</div>
                  <div className="trg-risk-strip-value">
                    {volatility != null ? volatility.toFixed(1) : "—"}
                    <span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.6rem", color: "var(--dash-muted)", marginLeft: 2 }}>%</span>
                  </div>
                  <div className="trg-risk-strip-sub">annualised</div>
                </div>
                <div className="trg-risk-strip-item">
                  <div className="trg-risk-strip-label">Beta</div>
                  <div className="trg-risk-strip-value">
                    {beta != null ? beta.toFixed(2) : "—"}
                  </div>
                  <div className="trg-risk-strip-sub">vs S&P 500</div>
                </div>
              </div>

              {/* Two gauges */}
              <div className="trg-gauges-row">
                <div className="trg-gauge-section">
                  <div className="trg-section-label">Risk-Adjusted Return vs Benchmark</div>
                  <SvgGauge
                    min={0} max={SHARPE_MAX}
                    value={sharpeGauge.value}
                    target={sharpeGauge.target}
                    colourFn={sharpeColour}
                    reading={sharpeGauge.value.toFixed(2)}
                    readingColor={sharpeReadColor}
                  />
                </div>
                <div className="trg-gauge-section">
                  <div className="trg-section-label">Risk vs Limit</div>
                  <SvgGauge
                    min={0} max={varMax}
                    value={varGauge.value}
                    target={varGauge.limit}
                    targetLabel={varGauge.limit != null
                      ? Math.abs(varGauge.limit) >= 1e6
                        ? (varGauge.limit / 1e6).toFixed(1) + "M"
                        : (varGauge.limit / 1e3).toFixed(0) + "K"
                      : ""}
                    colourFn={riskColour}
                    reading={
                      <>
                        {varGauge.value != null
                          ? Math.abs(varGauge.value) >= 1e6
                            ? (varGauge.value / 1e6).toFixed(1)
                            : (varGauge.value / 1e3).toFixed(1)
                          : "—"}
                        <span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.85rem", color: "var(--dash-muted)", marginLeft: 3 }}>
                          {varGauge.value != null ? (Math.abs(varGauge.value) >= 1e6 ? "M" : "K") : ""}
                        </span>
                      </>
                    }
                    readingColor={varReadColor}
                  />
                </div>
              </div>

              <div className="trg-gauge-note">
                Both gauges use the leftmost risk metric shown above
              </div>

              {/* Concentrations vs Limits */}
              <div className="trg-conc-section">
                <div className="trg-conc-header">
                  <div className="trg-conc-title">Concentrations vs Limits</div>
                  <button className="trg-expand-btn" onClick={() =>
                    openFs("Concentrations vs Limits",
                      <ConcContent rows={concRows} maxRatio={concMax} />
                    )
                  }>⛶</button>
                </div>
                <div className="trg-conc-rows">
                  {concRows.map((d) => {
                    const c = concColour(d.ratio);
                    const vp = (d.ratio / concMax * 100).toFixed(2);
                    const lp = (1 / concMax * 100).toFixed(2);
                    return (
                      <div key={d.category} className="trg-conc-row">
                        <div className="trg-conc-name">{d.category}</div>
                        <div style={{ flex: 1, position: "relative", height: 16 }}>
                          <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 1, background: "var(--dash-border)", transform: "translateY(-50%)" }} />
                          <div style={{ position: "absolute", top: "50%", left: 0, width: `${vp}%`, height: 2, background: c, opacity: 0.3, transform: "translateY(-50%)" }} />
                          <div style={{ position: "absolute", top: "50%", left: `${vp}%`, width: 8, height: 8, borderRadius: "50%", background: c, transform: "translate(-50%,-50%)", boxShadow: `0 0 5px ${c}88` }} />
                          <div style={{ position: "absolute", top: 2, bottom: 2, left: `${lp}%`, width: 1.5, background: "var(--dash-border2)", transform: "translateX(-50%)" }} />
                        </div>
                        <div className="trg-conc-val" style={{ color: c }}>
                          {d.ratio < 1 ? d.ratio.toFixed(2) : d.ratio.toFixed(1)}x
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── COL 3: Allocation ─────────────────────────────────────── */}
            <div className="trg-alloc-col-wrap">
              <div className="trg-card-header">
                <div className="trg-card-title">Allocation</div>
              </div>

              {/* Asset Allocation vs VaR Contribution */}
              <div className="trg-alloc-body">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
                  <div className="trg-alloc-name">Asset Allocation vs VaR Contribution</div>
                  <button className="trg-expand-btn" onClick={() =>
                    openFs("Asset Allocation vs VaR Contribution",
                      <AllocBarCanvas
                        levels={allocLevels}
                        theme={theme}
                        initialLevel={allocCurrentLevel}
                      />
                    )
                  }>⛶</button>
                </div>
                <AllocBarCanvas
                  levels={allocLevels}
                  theme={theme}
                  onLevelChange={setAllocCurrentLevel}
                />
              </div>

              <div style={{ height: 1, background: "var(--dash-border)", flexShrink: 0 }} />

              {/* Top Risk Contributors/Hedges */}
              <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <div style={{ padding: "0.65rem 1rem 0.3rem", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                  <div className="trg-alloc-name">Top Risk/Hedges — % VaR Contribution</div>
                  <button className="trg-expand-btn" onClick={() =>
                    openFs("Top Risk Contributors/Hedges — % VaR Contribution",
                      <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, justifyContent: "center" }}>
                        <TopRiskBar />
                      </div>
                    )
                  }>⛶</button>
                </div>
                <TopRiskBar />
              </div>
            </div>

            {/* ── COL 4: Chart + Table ──────────────────────────────────── */}
            <div className="trg-right-col">

              {/* Historical Market Value */}
              <div className="trg-card trg-chart-card">
                <div className="trg-chart-inner">
                  <div className="trg-chart-top">
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div className="trg-card-title">Historical Market Value</div>
                      <button className="trg-expand-btn" onClick={() =>
                        openFs("Historical Market Value",
                          <div style={{ flex: 1, minHeight: 400, display: "flex", flexDirection: "column" }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={chartPoints} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                  <linearGradient id="areaGradFs" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="var(--dash-blue)" stopOpacity={0.16} />
                                    <stop offset="100%" stopColor="var(--dash-blue)" stopOpacity={0} />
                                  </linearGradient>
                                </defs>
                                <CartesianGrid stroke="rgba(255,255,255,0.04)" strokeDasharray="3 3" vertical={false} />
                                <XAxis
                                  dataKey="label"
                                  tick={{ fill: "rgba(255,255,255,0.25)", fontSize: 8, fontFamily: "var(--dash-font-m)" }}
                                  axisLine={false} tickLine={false}
                                  interval="preserveStartEnd"
                                />
                                <YAxis
                                  tickFormatter={(v) => `${v}M`}
                                  tick={{ fill: "rgba(255,255,255,0.25)", fontSize: 8, fontFamily: "var(--dash-font-m)" }}
                                  axisLine={false} tickLine={false}
                                  width={40} domain={["auto", "auto"]}
                                />
                                <Tooltip content={<ChartTooltip />} />
                                <Area
                                  type="monotone" dataKey="value"
                                  stroke="var(--dash-blue)" strokeWidth={1.8}
                                  fill="url(#areaGradFs)" dot={false}
                                  activeDot={{ r: 3, fill: "var(--dash-blue)" }}
                                />
                                <Line
                                  type="monotone" dataKey="bmk"
                                  stroke="var(--dash-gold)" strokeWidth={1.5}
                                  strokeDasharray="5 4" dot={false}
                                  activeDot={{ r: 3, fill: "var(--dash-gold)" }}
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        )
                      }>⛶</button>
                    </div>
                    <div className="trg-period-btns">
                      {["1M", "3M", "1Y", "3Y", "ALL"].map((r) => (
                        <button
                          key={r}
                          className={`trg-period-btn${activeRange === r ? " active" : ""}`}
                          onClick={() => setActiveRange(r)}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="trg-chart-legend">
                    <div className="trg-legend-item">
                      <div style={{ width: 16, height: 2, background: "var(--dash-blue)", borderRadius: 1 }} />
                      Portfolio
                    </div>
                    <div className="trg-legend-item">
                      <div style={{ width: 16, height: 0, borderTop: "2px dashed var(--dash-gold)" }} />
                      Benchmark
                    </div>
                  </div>

                  <div style={{ flex: 1, minHeight: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartPoints} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="var(--dash-blue)" stopOpacity={0.16} />
                            <stop offset="100%" stopColor="var(--dash-blue)" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke="rgba(255,255,255,0.04)" strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="label"
                          tick={{ fill: "rgba(255,255,255,0.25)", fontSize: 8, fontFamily: "var(--dash-font-m)" }}
                          axisLine={false} tickLine={false}
                          interval="preserveStartEnd"
                        />
                        <YAxis
                          tickFormatter={(v) => `${v}M`}
                          tick={{ fill: "rgba(255,255,255,0.25)", fontSize: 8, fontFamily: "var(--dash-font-m)" }}
                          axisLine={false} tickLine={false}
                          width={40} domain={["auto", "auto"]}
                        />
                        <Tooltip content={<ChartTooltip />} />
                        <Area
                          type="monotone" dataKey="value"
                          stroke="var(--dash-blue)" strokeWidth={1.8}
                          fill="url(#areaGrad)" dot={false}
                          activeDot={{ r: 3, fill: "var(--dash-blue)" }}
                        />
                        <Line
                          type="monotone" dataKey="bmk"
                          stroke="var(--dash-gold)" strokeWidth={1.5}
                          strokeDasharray="5 4" dot={false}
                          activeDot={{ r: 3, fill: "var(--dash-gold)" }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Consolidated Accounts table */}
              <div className="trg-card trg-table-card">
                <div className="trg-table-inner">
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div className="trg-card-title">Consolidated Accounts</div>
                    <button className="trg-expand-btn" onClick={() =>
                      openFs("Consolidated Accounts",
                        <table className="trg-acct-table" style={{ marginTop: 8 }}>
                          <thead>
                            <tr>
                              <th style={{ width: "34%", textAlign: "left" }}>Broker</th>
                              <th style={{ width: "16%" }}>Mkt Val</th>
                              <th style={{ width: "16%" }}>1D Ret</th>
                              <th style={{ width: "17%" }}>VaR</th>
                              <th style={{ width: "17%" }}>Vol</th>
                            </tr>
                          </thead>
                          <tbody>
                            {brokerRows.map((b, i) => {
                              const dot = BROKER_COLORS[b.brokerName] || DEFAULT_BROKER_COLOR;
                              const rc = b.return1d >= 0 ? "var(--dash-green)" : "var(--dash-red)";
                              return (
                                <tr key={i}>
                                  <td>
                                    <div className="trg-broker-cell">
                                      <div className="trg-broker-dot" style={{ background: dot, boxShadow: `0 0 5px ${dot}88` }} />
                                      <span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.8rem", color: "var(--dash-text)" }}>{b.brokerName}</span>
                                    </div>
                                  </td>
                                  <td style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.78rem", color: "var(--dash-text)" }}>{(b.marketValue / 1e6).toFixed(1)}M</td>
                                  <td style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.78rem", color: rc }}>{b.return1d >= 0 ? "+" : ""}{b.return1d.toFixed(2)}%</td>
                                  <td style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.78rem", color: "var(--dash-text)" }}>{(b.var1d / 1e6).toFixed(1)}M</td>
                                  <td style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.78rem", color: "var(--dash-text)" }}>{b.volatility.toFixed(1)}%</td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.6rem", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--dash-text)" }}>Total</td>
                              <td style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.78rem", fontWeight: 500, color: "var(--dash-text)" }}>{(totalMV / 1e6).toFixed(1)}M</td>
                              <td />
                              <td style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.78rem", fontWeight: 500, color: "var(--dash-text)" }}>{(totalVar / 1e6).toFixed(1)}M</td>
                              <td />
                            </tr>
                          </tfoot>
                        </table>
                      )
                    }>⛶</button>
                  </div>
                  <table className="trg-acct-table">
                    <thead>
                      <tr>
                        <th style={{ width: "34%", textAlign: "left" }}>Broker</th>
                        <th style={{ width: "16%" }}>Mkt Val</th>
                        <th style={{ width: "16%" }}>1D Ret</th>
                        <th style={{ width: "17%" }}>VaR</th>
                        <th style={{ width: "17%" }}>Vol</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brokerRows.map((b, i) => {
                        const dot = BROKER_COLORS[b.brokerName] || DEFAULT_BROKER_COLOR;
                        const rc = b.return1d >= 0 ? "var(--dash-green)" : "var(--dash-red)";
                        return (
                          <tr key={i}>
                            <td>
                              <div className="trg-broker-cell">
                                <div className="trg-broker-dot" style={{ background: dot, boxShadow: `0 0 5px ${dot}88` }} />
                                <span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.76rem", fontWeight: 500, color: "var(--dash-text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {b.brokerName}
                                </span>
                              </div>
                            </td>
                            <td style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.75rem", color: "var(--dash-text)" }}>
                              {(b.marketValue / 1e6).toFixed(1)}M
                            </td>
                            <td style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.75rem", color: rc }}>
                              {b.return1d >= 0 ? "+" : ""}{b.return1d.toFixed(2)}%
                            </td>
                            <td style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.75rem", color: "var(--dash-text)" }}>
                              {(b.var1d / 1e6).toFixed(1)}M
                            </td>
                            <td style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.75rem", color: "var(--dash-text)" }}>
                              {b.volatility.toFixed(1)}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.6rem", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--dash-text)" }}>
                          Total
                        </td>
                        <td style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.78rem", fontWeight: 500, color: "var(--dash-text)" }}>
                          {totalMV > 0 ? `${(totalMV / 1e6).toFixed(1)}M` : "—"}
                        </td>
                        <td />
                        <td style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.78rem", fontWeight: 500, color: "var(--dash-text)" }}>
                          {totalVar > 0 ? `${(totalVar / 1e6).toFixed(1)}M` : "—"}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

            </div>{/* /right-col */}

          </div>{/* /main-grid */}
        </main>

        {/* ── Fullscreen overlay ── */}
        {fsPanel && (
          <div className="trg-fs-overlay active" onClick={(e) => { if (e.target === e.currentTarget) closeFs(); }}>
            <div className="trg-fs-panel">
              <div className="trg-fs-header">
                <div className="trg-fs-title">{fsPanel.title}</div>
                <button className="trg-fs-close" onClick={closeFs}>✕ close</button>
              </div>
              <div className="trg-fs-body">
                {fsPanel.content}
              </div>
            </div>
          </div>
        )}

      </div>{/* /trg-shell */}
    </>
  );
}
